import { useState } from 'react';

import { adminCases, adminInsights, adminServices } from '../api/admin';
import ArticleBody from '../components/ArticleBody';
import type { Insight, InsightBlock } from '../types/content';
import { AreaField, ListField, SelectField, TextField, ToggleField } from './fields';
import PreviewOverlay from './PreviewOverlay';
import { useCollection } from './useCollection';
import { useDirty, useUnsavedChanges } from './useUnsavedChanges';

type BlockType = 'paragraph' | 'heading' | 'list';
type BlockDraft = { type: BlockType; text: string; items: string[]; textEn: string; itemsEn: string[] };
type InsightDraft = Omit<Insight, 'body'> & { body: BlockDraft[] };

const BLOCK_TYPES = [
  { value: 'paragraph', label: 'Stycke' },
  { value: 'heading', label: 'Mellanrubrik' },
  { value: 'list', label: 'Punktlista' }
];

const EMPTY: InsightDraft = {
  slug: '',
  title: '',
  topic: '',
  date: null,
  readingTime: '',
  excerpt: '',
  published: false,
  relatedCaseId: null,
  relatedCaseTitle: null,
  relatedServiceId: null,
  relatedServiceTitle: null,
  body: [],
  titleEn: '',
  excerptEn: ''
};

function toDraft(insight: Insight): InsightDraft {
  return {
    ...insight,
    readingTime: insight.readingTime ?? '',
    titleEn: insight.titleEn ?? '',
    excerptEn: insight.excerptEn ?? '',
    body: insight.body.map((block) => ({
      type: block.type,
      text: block.text ?? '',
      items: block.items ?? [],
      textEn: block.textEn ?? '',
      itemsEn: block.itemsEn ?? []
    }))
  };
}

function toPayload(draft: InsightDraft): Insight {
  const body = draft.body.map((block) => {
    if (block.type === 'list') {
      return {
        type: 'list',
        text: null,
        items: block.items,
        textEn: '',
        itemsEn: block.itemsEn
      } as InsightBlock;
    }
    return {
      type: block.type,
      text: block.text,
      items: [],
      textEn: block.textEn,
      itemsEn: []
    } as InsightBlock;
  });

  return {
    ...draft,
    date: draft.date || null,
    readingTime: draft.readingTime || null,
    body
  };
}

export default function InsightsPanel() {
  const { items, error, reload } = useCollection(adminInsights.list);
  const services = useCollection(adminServices.list);
  const cases = useCollection(adminCases.list);
  const { confirmLeave } = useUnsavedChanges();
  const [draft, setDraft] = useState<InsightDraft | null>(null);
  const [original, setOriginal] = useState<InsightDraft | null>(null);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useDirty(draft !== null && JSON.stringify(draft) !== JSON.stringify(original));

  const serviceOptions = [
    { value: '', label: 'Ingen koppling' },
    ...(services.items ?? []).map((service) => ({ value: service.id, label: service.title }))
  ];
  const caseOptions = [
    { value: '', label: 'Ingen koppling' },
    ...(cases.items ?? []).map((item) => ({ value: item.id, label: item.title }))
  ];

  function open(item: InsightDraft, slug: string | null) {
    if (!confirmLeave()) return;
    setDraft(item);
    setOriginal(item);
    setEditingSlug(slug);
    setSaveError(null);
  }

  function close() {
    setDraft(null);
    setOriginal(null);
    setEditingSlug(null);
  }

  function change(patch: Partial<InsightDraft>) {
    setDraft((current) => (current ? { ...current, ...patch } : current));
  }

  function changeBlock(index: number, patch: Partial<BlockDraft>) {
    setDraft((current) => {
      if (!current) return current;
      const body = current.body.map((block, i) => (i === index ? { ...block, ...patch } : block));
      return { ...current, body };
    });
  }

  function moveBlock(index: number, direction: -1 | 1) {
    setDraft((current) => {
      if (!current) return current;
      const target = index + direction;
      if (target < 0 || target >= current.body.length) return current;
      const body = [...current.body];
      [body[index], body[target]] = [body[target], body[index]];
      return { ...current, body };
    });
  }

  async function save() {
    if (!draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = toPayload(draft);
      if (editingSlug) {
        await adminInsights.update(editingSlug, payload);
      } else {
        await adminInsights.create(payload);
      }
      close();
      reload();
    } catch {
      setSaveError('Kunde inte spara. Kontrollera att slug:en är unik och att alla fält är ifyllda.');
    } finally {
      setSaving(false);
    }
  }

  async function remove(insight: Insight) {
    if (!window.confirm(`Ta bort artikeln "${insight.title}"?`)) return;
    await adminInsights.remove(insight.slug);
    if (editingSlug === insight.slug) {
      close();
    }
    reload();
  }

  return (
    <>
      <div className={draft ? 'admin-layout is-editing' : 'admin-layout'}>
        <div className="admin-column">
          <div className="admin-head">
            <h2>Insights</h2>
            <button
              className="button primary small"
              type="button"
              onClick={() => open({ ...EMPTY, body: [] }, null)}
            >
              Ny artikel
            </button>
          </div>

          {error && <div className="state is-error">{error}</div>}

          <div className="admin-list">
            {items?.map((insight) => (
              <div className="admin-row" key={insight.slug}>
                <div>
                  <strong>{insight.title}</strong>
                  <div className="meta">
                    {insight.topic} ·{' '}
                    {insight.published ? `Publicerad ${insight.date ?? ''}` : 'Utkast'} · {insight.slug}
                  </div>
                </div>
                <div className="admin-actions">
                  <button
                    className="button secondary small"
                    type="button"
                    onClick={() => open(toDraft(insight), insight.slug)}
                  >
                    Redigera
                  </button>
                  <button className="button danger small" type="button" onClick={() => remove(insight)}>
                    Ta bort
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {draft && (
          <div className="admin-panel">
            <div className="panel-head">
              <h3>{editingSlug ? `Redigerar ${editingSlug}` : 'Ny artikel'}</h3>
              <button className="button secondary small" type="button" onClick={() => setPreview(true)}>
                Förhandsgranska
              </button>
            </div>
          <TextField
            label="Slug"
            hint="(används i URL:en)"
            value={draft.slug}
            onChange={(value) => change({ slug: value })}
          />
          <TextField label="Rubrik" value={draft.title} onChange={(value) => change({ title: value })} />
          <div className="field-row">
            <TextField
              label="Ämne"
              hint="(t.ex. Transformation, Architecture)"
              value={draft.topic}
              onChange={(value) => change({ topic: value })}
            />
            <TextField
              label="Lästid"
              hint="(t.ex. 5 min)"
              value={draft.readingTime ?? ''}
              onChange={(value) => change({ readingTime: value })}
            />
          </div>
          <div className="field-row">
            <TextField
              label="Publiceringsdatum"
              type="date"
              value={draft.date ?? ''}
              onChange={(value) => change({ date: value || null })}
            />
            <ToggleField
              label="Publicerad"
              checked={draft.published}
              onChange={(checked) => change({ published: checked })}
            />
          </div>
          <AreaField
            label="Ingress"
            value={draft.excerpt}
            onChange={(value) => change({ excerpt: value })}
          />
          <div className="lang-section">
            <h4>
              Engelska <span className="hint">(valfritt – tomma fält visar svenskan)</span>
            </h4>
            <TextField
              label="Rubrik"
              value={draft.titleEn ?? ''}
              onChange={(value) => change({ titleEn: value })}
            />
            <AreaField
              label="Ingress"
              value={draft.excerptEn ?? ''}
              onChange={(value) => change({ excerptEn: value })}
            />
          </div>
          <div className="field-row">
            <SelectField
              label="Relaterat case"
              value={draft.relatedCaseId ?? ''}
              options={caseOptions}
              onChange={(value) => change({ relatedCaseId: value || null })}
            />
            <SelectField
              label="Relaterad tjänst"
              value={draft.relatedServiceId ?? ''}
              options={serviceOptions}
              onChange={(value) => change({ relatedServiceId: value || null })}
            />
          </div>

          <div className="block-editor">
            <div className="admin-head">
              <h4>Brödtext</h4>
              <button
                className="button secondary small"
                type="button"
                onClick={() =>
                  change({
                    body: [...draft.body, { type: 'paragraph', text: '', items: [], textEn: '', itemsEn: [] }]
                  })
                }
              >
                Lägg till block
              </button>
            </div>

            {draft.body.map((block, index) => (
              <div className="block" key={index}>
                <div className="block-head">
                  <SelectField
                    label={`Block ${index + 1}`}
                    value={block.type}
                    options={BLOCK_TYPES}
                    onChange={(value) => changeBlock(index, { type: value as BlockType })}
                  />
                  <div className="admin-actions">
                    <button
                      className="button secondary small"
                      type="button"
                      aria-label="Flytta upp"
                      onClick={() => moveBlock(index, -1)}
                    >
                      ↑
                    </button>
                    <button
                      className="button secondary small"
                      type="button"
                      aria-label="Flytta ned"
                      onClick={() => moveBlock(index, 1)}
                    >
                      ↓
                    </button>
                    <button
                      className="button danger small"
                      type="button"
                      onClick={() => change({ body: draft.body.filter((_, i) => i !== index) })}
                    >
                      Ta bort
                    </button>
                  </div>
                </div>

                {block.type === 'list' ? (
                  <>
                    <ListField
                      label="Punkter"
                      value={block.items}
                      onChange={(value) => changeBlock(index, { items: value })}
                    />
                    <ListField
                      label="Punkter (EN)"
                      value={block.itemsEn}
                      onChange={(value) => changeBlock(index, { itemsEn: value })}
                    />
                  </>
                ) : (
                  <>
                    <AreaField
                      label={block.type === 'heading' ? 'Rubriktext' : 'Text'}
                      value={block.text}
                      onChange={(value) => changeBlock(index, { text: value })}
                    />
                    <AreaField
                      label={block.type === 'heading' ? 'Rubriktext (EN)' : 'Text (EN)'}
                      value={block.textEn}
                      onChange={(value) => changeBlock(index, { textEn: value })}
                    />
                  </>
                )}
              </div>
            ))}
          </div>

          {saveError && <div className="state is-error">{saveError}</div>}

          <div className="admin-actions">
            <button className="button primary" type="button" onClick={save} disabled={saving}>
              {saving ? 'Sparar…' : 'Spara'}
            </button>
            <button className="button secondary" type="button" onClick={() => confirmLeave() && close()}>
              Avbryt
            </button>
          </div>
        </div>
        )}
      </div>

      {draft && preview && (
        <PreviewOverlay title="Så visas artikeln" onClose={() => setPreview(false)}>
          <section className="page-hero">
            <p className="eyebrow">{draft.topic || 'Ämne saknas'}</p>
            <h1>{draft.title || 'Rubrik saknas'}</h1>
            <p className="article-meta">
              {draft.date ?? 'Inget datum'} · {draft.readingTime || 'ingen lästid'} läsning
            </p>
          </section>
          <article className="article">
            <ArticleBody blocks={toPayload(draft).body} />
          </article>
        </PreviewOverlay>
      )}
    </>
  );
}
